/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import findLastIndex from 'lodash/findLastIndex';
import clsx from 'clsx';

import {
  addWelcome,
  selectCreatorMode,
  selectDocumentVersionById,
  selectIsAnyMessageFlipped,
  selectIsSearching,
  unsavedUpdatePageFhir,
} from './chatSlice';
import { AudienceType, MessageType, PageFhirType, PageType } from '../algorithm/types';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import Loading from '../Login/Loading';
import { MessageErrorBoundary } from '../ErrorScreen';
import { Message } from './Message';
import { snackNotifications } from '../notification/SnackBarUtils';
import styles from './Chat.module.scss';
import GuideNavigationButtons from './components/Audience/GuideNavigationButtons';
import {
  selectActiveFhirSidebarMessageIndex,
  selectHasFhirPatientData,
  selectIsFhirVisible,
  selectIsLoadingFhirPatientData,
  selectIsPatientChartPaneVisible,
  selectPatientContext,
  setIsFhirFilterVisible,
} from '../Fhir/fhirSlice';
import { selectPearlUserInfo, selectUserFlags } from '../Login/userSlice';
import { PatientSummary } from './components/PatientSummary/PatientSummary';
import { ReturnToHereButtonList } from './components/ReturnToHereButton';
import { selectPatientSummaryState } from './components/PatientSummary/patientSummarySlice';
import {
  canEditDocumentVersion,
  isTeamsUser,
  useEnableEpicEmbeddedView,
  useIsConsumerWebUser,
  useViewFhirUserFlag,
} from '../permissions';
import { ThreePaneViewToggleButtons } from './components/ThreePaneView/ToggleButtons';
import { CurrentFhirPatientContainer } from '../medicine/CurrentFhirPatientContainer';
import { usePatientSummary } from './hooks/ChatContext';
import FhirPatientComponent from './components/FhirPatientComponent';
import FhirFilterAndSummaryButtonContainer from './FhirFilterAndSummaryButtonContainer';
import AutocompleteRenderer from '../Autocomplete/AutocompleteRenderer';
import { SearchInput } from './components/SearchInput';
import { FhirSidebar } from '../Fhir/FhirSidebar';
import { contextualizationTags } from '../Fhir/fhir_utils';
import { MessageProvider } from './hooks/MessageContext';

type ChatProps = {
  allowPageChangeRoot: boolean;
  allowPageDelete: boolean;
  audienceFilter: AudienceType;
  children?: ReactNode;
  className?: string;
  compact?: boolean;
  inAlgorithmView: boolean;
  messages: MessageType[];
  onKeyToggled: (page: PageType, messageIndex: number, key: string, value: string) => void;
  onTextInput?: (text: string, slug?: string) => Promise<void>;
  publishedOnly: boolean;
  style?: React.CSSProperties;
  chatRef?: React.RefObject<HTMLDivElement>;
};

export function Chat({
  allowPageChangeRoot,
  allowPageDelete,
  audienceFilter,
  children,
  className,
  compact,
  inAlgorithmView,
  messages,
  onKeyToggled,
  onTextInput,
  publishedOnly,
  style,
  chatRef,
}: ChatProps) {
  const divRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const flags = useAppSelector(selectUserFlags);
  const isCreatorMode = useAppSelector(selectCreatorMode);
  const documentVersionId = messages.find((m) => m.pages?.[0])?.pages![0]!.document_version_id;
  const documentVersion = useAppSelector((state) => selectDocumentVersionById(state, documentVersionId));
  const isSearching = useAppSelector(selectIsSearching);
  const patientContext = useAppSelector(selectPatientContext);
  const lastMessage = useRef<MessageType | undefined>(messages[messages.length - 1]);
  const latestMessageWithPages = [...messages].reverse().find((message) => !message.is_sent && message.pages?.length);
  const activeFhirSidebarMessageIndex = useAppSelector(selectActiveFhirSidebarMessageIndex);
  const activeSidebarMessageWithPages =
    activeFhirSidebarMessageIndex == null
      ? undefined
      : messages.find(
          (message) =>
            Math.floor(message.index) === activeFhirSidebarMessageIndex && !message.is_sent && message.pages?.length
        );
  const sidebarMessageWithPages = activeSidebarMessageWithPages || latestMessageWithPages;
  const sidebarDocumentVersionId = sidebarMessageWithPages?.pages?.[0]?.document_version_id;
  const sidebarDocumentVersion = useAppSelector((state) => selectDocumentVersionById(state, sidebarDocumentVersionId));

  const {
    showContextualization: showContextualization_,
    showTopic,
    showSummary,
  } = useAppSelector(selectPatientSummaryState);
  const isAnyMessageFlipped = useAppSelector(selectIsAnyMessageFlipped);
  const showContextualization = showContextualization_ && !isAnyMessageFlipped && !compact; // Don't show contextualization if any message is flipped, since it takes up a lot of space
  const fhirVisibility = useAppSelector(selectIsFhirVisible);
  const { summary, fetchingSummary } = useAppSelector(selectPatientSummaryState);

  const isPatientChartPaneVisible = useAppSelector(selectIsPatientChartPaneVisible);
  // applying styles based on no.of panes visible in chat
  const isAllPanesVisible = showSummary && showContextualization && fhirVisibility && isPatientChartPaneVisible;

  const showToggleButtons = usePatientSummary(); // show three pane view toggle buttons if patient summary is enabled.

  const viewFhirUserFlag = useViewFhirUserFlag();
  const isTeamsAppUser = isTeamsUser(pearlUserInfo);
  const isConsumerWebUser = useIsConsumerWebUser();
  const showFhirSidebar = useAppSelector(
    (state) => selectIsLoadingFhirPatientData(state) || selectHasFhirPatientData(state)
  );
  const sidebarPage = sidebarMessageWithPages?.pages?.[0];
  const sidebarCanEdit = canEditDocumentVersion(pearlUserInfo, sidebarDocumentVersion);
  const isSidebarDocumentContextualized = contextualizationTags?.some((value) =>
    sidebarDocumentVersion?.flags?.includes(value)
  );
  const isFhirFilterShown =
    viewFhirUserFlag && showFhirSidebar && showContextualization && isSidebarDocumentContextualized;

  useEffect(() => {
    // will check and add welcome message if required
    dispatch(addWelcome());
  }, [dispatch]);

  useEffect(() => {
    dispatch(setIsFhirFilterVisible(isFhirFilterShown));
  }, [dispatch, isFhirFilterShown]);

  useEffect(() => {
    if (messages.length && chatRef?.current) {
      const chatEl = chatRef.current;
      let lastScrollTop = chatEl.scrollTop;

      const handleFhirScroll = (e: Event) => {
        window.dispatchEvent(new CustomEvent('fhirSidebarScroll'));
        const fhirEl = document.getElementById('fhir-scroll-container');
        if (!fhirEl) return;

        const currentScrollTop = chatEl.scrollTop;
        const isScrollingDown = currentScrollTop > lastScrollTop;
        const isScrollingUp = currentScrollTop < lastScrollTop;
        const atBottom = Math.abs(fhirEl.scrollHeight - fhirEl.scrollTop - fhirEl.clientHeight) < 2;
        const atTop = fhirEl.scrollTop <= 2;

        if ((isScrollingDown && !atBottom) || (isScrollingUp && !atTop)) {
          fhirEl.scrollTop = currentScrollTop;
        }

        lastScrollTop = currentScrollTop;
      };

      chatEl.addEventListener('scroll', handleFhirScroll);

      return () => {
        chatEl.removeEventListener('scroll', handleFhirScroll);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    /* 'smooth' looks nice, but behaviour is a bit buggy */
    // This is buggy in Microsoft Teams
    if (lastMessage.current?.pages?.[0] !== messages[messages.length - 1]?.pages?.[0]) {
      (divRef as any)?.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      lastMessage.current = messages[messages.length - 1];
    }
  }, [messages]);

  // Remove any snack bar notifications regarding locked document when we navigate away
  useEffect(() => {
    return () => {
      snackNotifications.topClearAll();
    };
  }, []);

  // const scrollToDiv = (
  //   <div className={styles.scrollToDiv}>
  //     <div key="divref" ref={divRef} id="lastMessageToScrollTo" />
  //   </div>
  // );

  // Say we have:
  //  Sent message 1
  // Received message 2
  //  Sent message 3
  // Received message 4
  // Received message 5

  // We want to scroll to the bottom of Sent message 3, not the bottom of Received message 5.
  const messageIndexToScrollTo = findLastIndex(
    messages,
    (m) => !!m.is_sent && !(m.extra && m.extra.type === 'OPEN_LINK' && m.hide_sent)
  );
  const lastMessageIndex = messages.length - 1;

  const shouldUseAlgoliaAutocomplete = !isCreatorMode || flags?.creator_algolia_search;

  const enableEpicEmbeddedView = useEnableEpicEmbeddedView();
  useEffect(() => {
    if (!messages.length) return;

    requestAnimationFrame(() => {
      divRef.current?.scrollIntoView({
        behavior: isTeamsAppUser ? 'auto' : 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    });
  }, [messages.length, messageIndexToScrollTo, isTeamsAppUser]);

  const handleUpdateFhir = useCallback(
    async (fhir: PageFhirType, pageId: string) => {
      if (isCreatorMode) {
        return;
      }
      if (pageId === 'LANDING') {
        throw new Error('Cannot update fhir for the LANDING page');
      }
      if (!sidebarPage) {
        throw new Error('Cannot update fhir for a page that does not exist');
      }
      dispatch(
        unsavedUpdatePageFhir({
          id: pageId,
          document_version_id: sidebarPage.document_version_id,
          idsToFhir: fhir.idsToFhir,
        })
      );
    },
    [dispatch, isCreatorMode, sidebarPage]
  );

  // Note, don't put useTitle here - ChatComponent includes useSearchParamsDocument which will set the browser title
  return (
    // In consumer, the search and actions container is inside the chatcomponent but in creator mode its above the chat. Adjust the height accordingly.
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}
      className={className}
      id="audienceSection"
    >
      <div className={compact ? styles.compactChatArea : styles.chatArea}>
        {(isConsumerWebUser || showToggleButtons || (viewFhirUserFlag && !isCreatorMode)) && (
          <div className={styles.searchAndActionsContainer} data-mode-creator="false">
            {isConsumerWebUser && (
              <div className={styles.guideNavigation}>
                <GuideNavigationButtons currentGuideTitle={documentVersion?.title} />
              </div>
            )}
            {/* use algolia autocomplete only if mode is consumer */}
            {/* Show search here only for teams app user. For others its in toplayout */}
            {onTextInput &&
              isTeamsAppUser &&
              (shouldUseAlgoliaAutocomplete ? (
                <AutocompleteRenderer
                  onEnter={onTextInput}
                  publishedOnly={publishedOnly}
                  audience={audienceFilter}
                  className="chat-input-autocomplete"
                />
              ) : (
                <SearchInput
                  onEnter={onTextInput}
                  publishedOnly={publishedOnly}
                  audience={audienceFilter}
                  isSearching={isSearching}
                />
              ))}

            {viewFhirUserFlag && !isCreatorMode && <FhirPatientComponent patientContext={patientContext} />}
            <FhirFilterAndSummaryButtonContainer documentVersion={documentVersion} />
            {!isSearching && showToggleButtons && (
              <ThreePaneViewToggleButtons
                title={documentVersion?.title}
                props={summary && !fetchingSummary ? 'view_summary' : undefined}
              />
            )}
          </div>
        )}

        <div className={styles.horizontalDivider} />

        {messages.length || children ? (
          <>
            <div
              className={clsx(styles.chatAndSummaryContainer, 'chatAndSummaryContainer')}
              data-has-patient={patientContext?.patient ? 'true' : 'false'}
              data-fhir-position-root="true"
            >
              <div
                ref={chatRef}
                className={clsx(styles.messages, isAllPanesVisible && styles.messageWithAllPanes, 'showScrollbars')}
                id="messages"
                data-num-messages={messages.length}
              >
                {children}
                {messages.length === 0 && isSearching && <Loading />}
                {/* {messageIndexToScrollTo === -1 && scrollToDiv} */}
                {messages.map(
                  (message, messageIndex) =>
                    // We don't want to show One exact match found with audience labels.
                    message?.extra?.type !== 'MATCH_FOUND' && (
                      <div
                        key={messageIndex}
                        style={{
                          position: 'relative',
                          width:
                            !showTopic && !showContextualization ? '0px' : '' /* For offsetHeight in FhirSidebar */,
                        }}
                        // Add the ref directly to the div to avoid creating an extra div element.
                        // This helps with intermittent issues with scrollIntoView not working as expected.
                        ref={messageIndex === messageIndexToScrollTo ? divRef : undefined}
                        id="messagesContainer"
                      >
                        {/* {messageIndex === messageIndexToScrollTo && scrollToDiv} */}
                        <MessageErrorBoundary>
                          <Message
                            compact={compact}
                            allowPageDelete={allowPageDelete}
                            allowPageChangeRoot={allowPageChangeRoot}
                            message={message}
                            isLastMessage={messageIndex === lastMessageIndex}
                            inAlgorithmView={inAlgorithmView}
                            onKeyToggled={(page, key, value) => onKeyToggled(page, messageIndex, key, value)}
                          />
                        </MessageErrorBoundary>
                      </div>
                    )
                )}
                <ReturnToHereButtonList lastMessageIndex={lastMessageIndex} />
              </div>
              {patientContext?.patient && sidebarMessageWithPages?.pages?.length ? (
                <>
                  {showContextualization && sidebarMessageWithPages && sidebarDocumentVersionId && (
                    <MessageProvider message={sidebarMessageWithPages}>
                      <FhirSidebar
                        readOnly={!sidebarCanEdit || isCreatorMode}
                        documentVersionId={sidebarDocumentVersionId}
                        onChangeFhir={handleUpdateFhir}
                      />
                    </MessageProvider>
                  )}
                  {showContextualization && <CurrentFhirPatientContainer documentVersion={documentVersion} />}
                  {!enableEpicEmbeddedView && <PatientSummary />}
                </>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
      <span id="chat-doc-title" className="chatDocTitle" style={{ display: 'none' }}>
        {documentVersion?.title}
      </span>
    </div>
  );
}
